import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { authApi, formatApiError } from "../lib/api";

const COPY = {
  en: {
    heading: "Forgot your password?",
    tagline: "Tell us the address you signed up with and we will send a link to set a new one.",
    email: "Email address",
    submit: "Send reset link",
    sending: "Sending…",
    back: "Back to sign in",
    sentHeading: "Check your email",
    sentBody:
      "If that address has an account, a reset link is on its way. The link works once and expires in 30 minutes.",
    sentNote:
      "Nothing arrived? Check your spam folder, or try again in a few minutes.",
    again: "Use a different address",
  },
  ms: {
    heading: "Terlupa kata laluan?",
    tagline: "Beritahu alamat e-mel yang anda guna untuk mendaftar dan kami hantar pautan untuk tetapkan yang baharu.",
    email: "Alamat e-mel",
    submit: "Hantar pautan",
    sending: "Menghantar…",
    back: "Kembali ke log masuk",
    sentHeading: "Semak e-mel anda",
    sentBody:
      "Kalau alamat itu ada akaun, pautan sedang dalam perjalanan. Pautan itu sah sekali sahaja dan luput dalam 30 minit.",
    sentNote:
      "Tiada apa sampai? Semak folder spam, atau cuba lagi dalam beberapa minit.",
    again: "Guna alamat lain",
  },
};

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [lang, setLang] = useState(() => {
    try { return localStorage.getItem("bicarra.lang") === "ms" ? "ms" : "en"; }
    catch { return "en"; }
  });
  const t = COPY[lang];

  function pickLang(next) {
    setLang(next);
    try { localStorage.setItem("bicarra.lang", next); } catch { /* ignore */ }
  }

  async function onSubmit(e) {
    e.preventDefault();
    if (loading) return;
    setError("");
    setLoading(true);
    try {
      await authApi.forgotPassword(email.trim());
      /* The API answers the same way whether or not the address is registered,
         so this screen must not claim an account exists. */
      setSent(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="b2 min-h-screen bg-b2-canvas text-b2-ink font-sans flex flex-col">
      <header className="px-6 h-[72px] flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <img src="/brand/bicarra-icon.svg" alt="" className="w-[26px] h-[26px] rounded-[7px]" />
          <span className="text-[19px] font-bold tracking-[-0.03em] text-b2-ink lowercase">bicarra</span>
        </Link>
        <div className="flex items-center gap-1 text-[13px]">
          <button type="button" onClick={() => pickLang("en")}
                  className={lang === "en" ? "text-b2-ink font-semibold" : "text-b2-faint hover:text-b2-muted"}>
            English
          </button>
          <span className="text-b2-faint">/</span>
          <button type="button" onClick={() => pickLang("ms")}
                  className={lang === "ms" ? "text-b2-ink font-semibold" : "text-b2-faint hover:text-b2-muted"}>
            BM
          </button>
        </div>
      </header>

      <main className="flex-1 flex items-start justify-center px-6 pt-10 pb-20">
        <div className="w-full max-w-[400px]">
          {!sent ? (
            <>
              <h1 className="text-[28px] leading-[1.2] font-bold tracking-[-0.03em]">{t.heading}</h1>
              <p className="mt-3 text-[15px] leading-relaxed text-b2-inksoft">{t.tagline}</p>

              <form onSubmit={onSubmit} className="mt-8 space-y-4">
                <div>
                  <label htmlFor="fp-email" className="block text-[13.5px] font-medium text-b2-muted mb-1.5">
                    {t.email}
                  </label>
                  <input
                    id="fp-email" type="email" required autoFocus
                    value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@business.com" data-testid="forgot-email"
                    className="w-full h-12 px-4 rounded-[10px] bg-white border border-b2-line
                               text-[15px] text-b2-ink placeholder:text-b2-faint
                               focus:outline-none focus:border-b2-indigo transition-colors"
                  />
                </div>

                {error && (
                  <p className="text-[13.5px] text-b2-attention bg-b2-attentionwash rounded-[10px] px-3.5 py-2.5">
                    {error}
                  </p>
                )}

                <button
                  type="submit" disabled={loading} data-testid="forgot-submit"
                  className="w-full h-12 rounded-[10px] bg-b2-indigo hover:bg-b2-indigohover
                             disabled:opacity-60 text-white text-[15px] font-semibold
                             inline-flex items-center justify-center gap-2 transition-colors"
                >
                  {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                  {loading ? t.sending : t.submit}
                </button>
              </form>
            </>
          ) : (
            <div data-testid="forgot-sent">
              <h1 className="text-[28px] leading-[1.2] font-bold tracking-[-0.03em]">{t.sentHeading}</h1>
              <p className="mt-3 text-[15px] leading-relaxed text-b2-inksoft">{t.sentBody}</p>
              <p className="mt-4 text-[13.5px] leading-relaxed text-b2-muted">{t.sentNote}</p>
              <button
                type="button"
                onClick={() => { setSent(false); setEmail(""); }}
                className="mt-6 text-[14px] font-semibold text-b2-indigo hover:text-b2-indigohover"
              >
                {t.again}
              </button>
            </div>
          )}

          <p className="mt-8 text-[14px] text-b2-inksoft">
            <Link to="/login" className="text-b2-indigo hover:underline">{t.back}</Link>
          </p>
        </div>
      </main>
    </div>
  );
}
